import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { ClientUsageMetrics } from '@/types/license';

interface UsageMetricsFilters {
  clientId?: string;
  startDate?: string;
  endDate?: string;
  period?: 'daily' | 'weekly' | 'monthly';
}

export const useUsageMetrics = (filters?: UsageMetricsFilters) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const {
    data: usageMetrics = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['usage-metrics', filters],
    queryFn: async () => {
      let query = supabase
        .from('client_usage_metrics')
        .select(`
          *,
          client:clients (
            id,
            name
          )
        `)
        .order('metric_date', { ascending: false });

      // Aplicar filtros
      if (filters?.clientId && filters.clientId !== 'all') {
        query = query.eq('client_id', filters.clientId);
      }

      if (filters?.startDate) {
        query = query.gte('metric_date', filters.startDate);
      }

      if (filters?.endDate) {
        query = query.lte('metric_date', filters.endDate);
      }

      const { data, error } = await query;
      if (error) throw error;

      return data as (ClientUsageMetrics & {
        client: { id: string; name: string };
      })[];
    }
  });

  // Buscar limites de licença para comparação
  const {
    data: licenseLimits = {},
    isLoading: limitsLoading
  } = useQuery({
    queryKey: ['license-limits'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('client_licenses')
        .select(`
          client_id,
          license_plan:license_plans (
            name,
            max_technicians,
            max_users
          )
        `)
        .eq('status', 'active');

      if (error) throw error;

      // Converter para objeto com client_id como chave
      const limits: Record<string, {
        plan_name: string;
        max_technicians: number;
        max_users: number | null;
      }> = {};

      data?.forEach(license => {
        if (license.client_id && license.license_plan) {
          limits[license.client_id] = {
            plan_name: license.license_plan.name,
            max_technicians: license.license_plan.max_technicians,
            max_users: license.license_plan.max_users
          };
        }
      });

      return limits;
    }
  });

  // Função para coletar métricas atuais
  const collectCurrentMetrics = useMutation({
    mutationFn: async (clientId?: string) => {
      // Se não especificado, coleta para todos os clientes
      const clientsQuery = clientId 
        ? supabase.from('clients').select('id').eq('id', clientId)
        : supabase.from('clients').select('id').eq('status', 'active');

      const { data: clients, error: clientsError } = await clientsQuery;
      if (clientsError) throw clientsError;

      const metricsPromises = clients.map(async (client) => {
        // Contar usuários ativos
        const { data: users, error: usersError } = await supabase
          .from('users')
          .select('role, updated_at')
          .eq('client_id', client.id)
          .eq('status', 'active');

        if (usersError) throw usersError;

        const totalTechnicians = users?.filter(u => u.role === 'technician').length || 0;
        const totalUsers = users?.filter(u => ['user', 'client_admin'].includes(u.role)).length || 0;

        // Contar usuários ativos nos últimos 30 dias
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

        const activeTechnicians = users?.filter(u => 
          u.role === 'technician' && 
          new Date(u.updated_at) >= thirtyDaysAgo
        ).length || 0;

        const activeUsers = users?.filter(u => 
          ['user', 'client_admin'].includes(u.role) && 
          new Date(u.updated_at) >= thirtyDaysAgo
        ).length || 0;

          // Contar tickets criados e resolvidos hoje
          const today = new Date().toISOString().split('T')[0];
          
          const { data: ticketsCreated, error: createdError } = await supabase
            .from('incidentes')
            .select('id')
            .eq('client_id', client.id)
            .gte('criado_em', `${today}T00:00:00Z`)
            .lt('criado_em', `${today}T23:59:59Z`);

          if (createdError) throw createdError;

          const { data: ticketsResolved, error: resolvedError } = await supabase
            .from('incidentes')
            .select('id')
            .eq('client_id', client.id)
            .eq('status', 'resolvida')
            .gte('atualizado_em', `${today}T00:00:00Z`)
            .lt('atualizado_em', `${today}T23:59:59Z`);

          if (resolvedError) throw resolvedError;

        // Inserir ou atualizar métricas
        const metricsData = {
          client_id: client.id,
          metric_date: today,
          total_technicians: totalTechnicians,
          total_users: totalUsers,
          active_technicians: activeTechnicians,
          active_users: activeUsers,
          tickets_created: ticketsCreated?.length || 0,
          tickets_resolved: ticketsResolved?.length || 0,
          storage_used_mb: 0 // TODO: Implementar cálculo de storage
        };

        const { data, error } = await supabase
          .from('client_usage_metrics')
          .upsert(metricsData, {
            onConflict: 'client_id,metric_date'
          })
          .select()
          .single();

        if (error) throw error;
        return data;
      });

      const results = await Promise.all(metricsPromises);
      return results;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usage-metrics'] });
      queryClient.invalidateQueries({ queryKey: ['license-usage-dashboard'] });
      toast({
        title: "Métricas atualizadas",
        description: "Métricas de uso coletadas com sucesso.",
      });
    },
    onError: (error: any) => {
      console.error('Error collecting metrics:', error);
      toast({
        title: "Erro ao coletar métricas",
        description: "Não foi possível coletar as métricas de uso.",
        variant: "destructive",
      });
    }
  });

  // Função para calcular porcentagem de uso
  const calculateUsagePercentage = (current: number, max: number | null) => {
    if (!max || max <= 0) return 0;
    return Math.min((current / max) * 100, 100);
  };

  // Dados para gráficos
  const getChartData = () => {
    return usageMetrics.map(metric => ({
      client: metric.client?.name || 'Cliente Desconhecido',
      date: metric.metric_date,
      technicians: metric.total_technicians,
      users: metric.total_users,
      tickets_created: metric.tickets_created,
      tickets_resolved: metric.tickets_resolved,
      active_technicians: metric.active_technicians,
      active_users: metric.active_users
    }));
  };

  return {
    usageMetrics,
    licenseLimits,
    isLoading: isLoading || limitsLoading,
    error,
    refetch,
    collectCurrentMetrics: collectCurrentMetrics.mutate,
    isCollecting: collectCurrentMetrics.isPending,
    calculateUsagePercentage,
    getChartData
  };
};